import React from 'react';
import PropTypes from 'prop-types';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import DataTable, { Column } from '../DataTable/index.jsx';
import Figure, { CurrencyFigure, PercentFigure } from '../Figure/index.jsx';

import RestClient from '@scripts/utilities/rest-client.js';

import ColumnLabels from '@data/nft-collections/columns.json';


var NameHeaderColumn = function() {
  return <th />;
}


var NameDetailColumn = function(props) {
  var content = (
    <span>{props.row.name}</span>
  );

  if (props.row.tooltip) {
    var overlay = (
      <Tooltip id={'nft-historical-overlay-' + props.row.id}>
        {props.row.tooltip}
      </Tooltip>
    );
    content = (
      <OverlayTrigger overlay={overlay}>
        {content}
      </OverlayTrigger>
    );
  }

  return (
    <th>
      {content}
    </th>
  );
};


var ETHHeaderColumn = function() {
  return <th className="text-right">ETH</th>;
}


var ETHDetailColumn = function(props) {
  return (
    <td className="text-right">
      {props.row.eth}
    </td>
  );
};


var USDHeaderColumn = function() {
  var content = (
    <span>USD</span>
  );

  var overlay = (
    <Tooltip id="nft-historical-overlay-usd">
      Calculated based on ETH’s current price.<br />
      (Note, ETH-denominated revenue is often swapped for stablecoins upon receipt.)
    </Tooltip>
  );
  content = (
    <OverlayTrigger overlay={overlay}>
      {content}
    </OverlayTrigger>
  );

  return (
    <th className="text-right">
      {content}
    </th>
  );
}


var USDDetailColumn = function(props) {
  return (
    <td className="text-right">
      {props.row.usd}
    </td>
  );
};


class NameColumn extends Column {
  get id() {
    return 'name';
  }

  get isConfigurable() {
    return false;
  }

  get DetailColumn() {
    return NameDetailColumn;
  }

  get HeaderColumn() {
    return NameHeaderColumn;
  }
}


class ETHColumn extends Column {
  get id() {
    return 'eth';
  }

  get isConfigurable() {
    return false;
  }

  get DetailColumn() {
    return ETHDetailColumn;
  }

  get HeaderColumn() {
    return ETHHeaderColumn;
  }
}


class USDColumn extends Column {
  get id() {
    return 'usd';
  }

  get isConfigurable() {
    return false;
  }

  get DetailColumn() {
    return USDDetailColumn;
  }

  get HeaderColumn() {
    return USDHeaderColumn;
  }
}


var columns = [
  new NameColumn(),
  new ETHColumn(),
  new USDColumn()
];


class NFTHistoricalRevenue extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      totalSupply: null,
      totalVolume: null,
      mintPrice: null,
      ethPriceMint: null,
      paymentTokenUSD: null,
      secondarySalesRoyalty: null
    };

    this.fetchError = this.fetchError.bind(this);
    this.fetchSuccess = this.fetchSuccess.bind(this);
  }

  fetch() {
    RestClient.get('/wp-json/sbm/v1/nfts/' + this.props.id + '/historical-revenue/')
      .then(this.fetchSuccess)
      .catch(this.fetchError);
  }

  fetchError(error) {
    console.error('Error', error);

    this.setState({ loading: false });
  }

  fetchSuccess(response) {
    this.setState({
      loading: false,
      totalSupply: response.totalSupply,
      totalVolume: response.totalVolume,
      mintPrice: response.mintPrice,
      ethPriceMint: response.ethPriceMint,
      paymentTokenUSD: response.paymentTokenUSD,
      secondarySalesRoyalty: response.secondarySalesRoyalty
    });
  }

  componentDidMount() {
    this.fetch();
  }

  render() {
    var result = null;

    if (! this.state.loading) {
      var rows = [];


      if (this.state.totalSupply && this.state.mintPrice && this.state.ethPriceMint) {
        var tmp = this.state.totalSupply * this.state.mintPrice;
        rows.push({
          id: 'mint',
          name: ColumnLabels.mint.name,
          tooltip: ColumnLabels.mint.description,
          eth: (
            <Figure amount={tmp} zeroIsBlank />
          ),
          usd: (
            <CurrencyFigure amount={tmp * this.state.ethPriceMint} zeroIsBlank />
          )
        });
      }
      if (this.state.totalVolume && this.state.paymentTokenUSD) {
        rows.push({
          id: 'volume',
          name: ColumnLabels.volume.name,
          tooltip: ColumnLabels.volume.description,
          eth: (
            <Figure amount={this.state.totalVolume} zeroIsBlank />
          ),
          usd: (
            <CurrencyFigure amount={this.state.totalVolume * this.state.paymentTokenUSD} zeroIsBlank />
          )
        });
      }
      if (this.state.secondarySalesRoyalty) {
        rows.push({
          id: 'royalty',
          name: ColumnLabels.royalty.name,
          tooltip: ColumnLabels.royalty.description,
          eth: (
            <PercentFigure
              amount={this.state.secondarySalesRoyalty * 100}
              highlightPositive={false}
              displaySign={false}
            />
          ),
          usd: (
            <PercentFigure
              amount={this.state.secondarySalesRoyalty * 100}
              highlightPositive={false}
              displaySign={false}
            />
          )
        });
      }
      if (this.state.secondarySalesRoyalty && this.state.totalVolume && this.state.paymentTokenUSD) {
        var tmp = this.state.totalVolume * this.state.secondarySalesRoyalty;
        rows.push({
          id: 'secondary-revenue',
          name: ColumnLabels.secondaryRevenue.name,
          tooltip: ColumnLabels.secondaryRevenue.description,
          eth: (
            <Figure amount={tmp} zeroIsBlank />
          ),
          usd: (
            <CurrencyFigure amount={tmp * this.state.paymentTokenUSD} zeroIsBlank />
          )
        });
      }
      if (this.state.totalSupply && this.state.mintPrice && this.state.ethPriceMint && this.state.secondarySalesRoyalty && this.state.totalVolume && this.state.paymentTokenUSD) {
        var primaryRevenueETH = this.state.totalSupply * this.state.mintPrice;
        var secondaryRevenueETH = this.state.totalVolume * this.state.secondarySalesRoyalty;
        rows.push({
          id: 'total',
          name: ColumnLabels.total.name,
          tooltip: ColumnLabels.total.description,
          eth: (
            <Figure amount={primaryRevenueETH + secondaryRevenueETH} zeroIsBlank />
          ),
          usd: (
            <CurrencyFigure amount={(primaryRevenueETH * this.state.ethPriceMint) + (secondaryRevenueETH * this.state.paymentTokenUSD)} zeroIsBlank />
          )
        });
      }

      if (rows.length) {
        result = (
          <DataTable columns={columns} rows={rows} />
        );
      }
    }


    return result;
  }
}


NFTHistoricalRevenue.propTypes = {
  id: PropTypes.number
};


export default NFTHistoricalRevenue;

